import { useEffect, useRef, useState } from 'react';
import type { ChartViewData } from '@shared/types/views';
import { CHART_COLORS, formatChartValue, hasChartValues, labelEvery, valueScale } from './chart-scale';

interface ChartViewProps {
  hideTitle?: boolean;
  data: ChartViewData;
}

/** Width used before the container is measured (and in tests). */
const DEFAULT_WIDTH = 640;
const AXIS = '#6a625a';
const GRID = '#ece3cf';
const truncate = (text: string, length: number) => (text.length > length ? `${text.slice(0, length - 1)}…` : text);
const color = (index: number) => CHART_COLORS[index % CHART_COLORS.length]!;

/** Line, bar and stacked bar charts: categories along the bottom, values up. */
const VerticalPlot = ({ data, width: WIDTH }: { data: ChartViewData; width: number }) => {
  const height = WIDTH < 480 ? 220 : 260;
  const [left, right, top, bottom] = [48, 12, data.unit ? 26 : 14, 30];
  const plotWidth = WIDTH - left - right;
  const plotHeight = height - top - bottom;
  const { min, max, ticks } = valueScale(data);
  const y = (value: number) => top + plotHeight * (1 - (value - min) / (max - min));
  const band = plotWidth / data.categories.length;
  const center = (index: number) => left + band * (index + 0.5);
  const every = labelEvery(data.categories.length, plotWidth / 72);
  const groupWidth = Math.min(band * 0.7, 56 * (data.kind === 'bar' ? data.series.length : 1));
  return (
    <svg viewBox={`0 0 ${WIDTH} ${height}`} width={WIDTH} height={height} className="block max-w-full" role="img" aria-label={data.title ?? 'Graphique'}>
      {data.unit && <text x={left - 8} y={top - 12} textAnchor="end" fontSize="11" fill={AXIS}>{data.unit}</text>}
      {ticks.map((tick) => (
        <g key={tick}>
          <line x1={left} x2={WIDTH - right} y1={y(tick)} y2={y(tick)} stroke={GRID} />
          <text x={left - 8} y={y(tick) + 4} textAnchor="end" fontSize="11" fill={AXIS}>{formatChartValue(tick, undefined, true)}</text>
        </g>
      ))}
      {data.categories.map((category, index) => (index % every === 0 ? (
        <text key={`${category}-${index}`} x={center(index)} y={height - 10} textAnchor="middle" fontSize="11" fill={AXIS}>
          <title>{category}</title>{truncate(category, 14)}
        </text>
      ) : null))}
      {data.kind === 'line' && data.series.map((series, seriesIndex) => {
        const segments: string[] = [];
        let current = '';
        series.values.forEach((value, index) => {
          if (value === null) { if (current) segments.push(current); current = ''; return; }
          current += `${current ? 'L' : 'M'}${center(index)},${y(value)}`;
        });
        if (current) segments.push(current);
        return (
          <g key={series.key}>
            {segments.map((d) => <path key={d} d={d} fill="none" stroke={color(seriesIndex)} strokeWidth="2.5" strokeLinejoin="round" />)}
            {series.values.map((value, index) => (value === null ? null : (
              <circle key={index} cx={center(index)} cy={y(value)} r={data.categories.length > 24 ? 2 : 3.5} fill={color(seriesIndex)}>
                <title>{`${series.label} — ${data.categories[index]} : ${formatChartValue(value, data.unit)}`}</title>
              </circle>
            )))}
          </g>
        );
      })}
      {data.kind !== 'line' && data.categories.map((category, index) => {
        let stack = 0;
        return data.series.map((series, seriesIndex) => {
          const value = series.values[index];
          if (value === null || value === undefined) return null;
          const barWidth = data.kind === 'bar' ? groupWidth / data.series.length : groupWidth;
          const x = data.kind === 'bar' ? center(index) - groupWidth / 2 + seriesIndex * barWidth : center(index) - groupWidth / 2;
          const from = data.kind === 'stacked_bar' ? stack : 0;
          const to = data.kind === 'stacked_bar' ? stack + Math.max(0, value) : value;
          if (data.kind === 'stacked_bar') stack = to;
          return (
            <rect key={`${category}-${series.key}`} x={x + 1} width={Math.max(1, barWidth - 2)} y={y(Math.max(from, to))}
              height={Math.max(1, Math.abs(y(from) - y(to)))} rx="2" fill={color(seriesIndex)}>
              <title>{`${series.label} — ${category} : ${formatChartValue(value, data.unit)}`}</title>
            </rect>
          );
        });
      })}
      {data.target && (
        <g>
          <line x1={left} x2={WIDTH - right} y1={y(data.target.value)} y2={y(data.target.value)} stroke="#211c1e" strokeDasharray="5 4" />
          <text x={WIDTH - right} y={y(data.target.value) - 6} textAnchor="end" fontSize="11" fill="#211c1e">
            {`${data.target.label} : ${formatChartValue(data.target.value, data.unit)}`}
          </text>
        </g>
      )}
    </svg>
  );
};

/** Ranking: one row per category, long labels on the left, bars to the right. */
const HorizontalPlot = ({ data, width: WIDTH }: { data: ChartViewData; width: number }) => {
  const row = 30;
  // The label column takes up to 40 % of the width; long labels are cut.
  const [left, right, top] = [Math.min(168, Math.round(WIDTH * 0.4)), 64, 6];
  const labelChars = Math.max(8, Math.floor((left - 10) / 6.5));
  const height = top * 2 + row * data.categories.length;
  const { max } = valueScale(data);
  const width = (value: number) => ((WIDTH - left - right) * Math.max(0, value)) / max;
  const bar = (row - 10) / data.series.length;
  return (
    <svg viewBox={`0 0 ${WIDTH} ${height}`} width={WIDTH} height={height} className="block max-w-full" role="img" aria-label={data.title ?? 'Graphique'}>
      {data.categories.map((category, index) => (
        <g key={`${category}-${index}`} transform={`translate(0 ${top + index * row})`}>
          <text x={left - 10} y={row / 2 + 4} textAnchor="end" fontSize="12" fill="#211c1e"><title>{category}</title>{truncate(category, labelChars)}</text>
          {data.series.map((series, seriesIndex) => {
            const value = series.values[index];
            if (value === null || value === undefined) return null;
            return (
              <g key={series.key}>
                <rect x={left} y={5 + seriesIndex * bar} width={Math.max(1, width(value))} height={Math.max(2, bar - 2)} rx="2" fill={color(seriesIndex)}>
                  <title>{`${series.label} — ${category} : ${formatChartValue(value, data.unit)}`}</title>
                </rect>
                <text x={left + width(value) + 6} y={5 + seriesIndex * bar + bar / 2 + 3} fontSize="11" fill={AXIS}>{formatChartValue(value, data.unit, true)}</text>
              </g>
            );
          })}
        </g>
      ))}
    </svg>
  );
};

/**
 * A chart drawn by Domino in its charter (layout lab, lot 2): line, bar,
 * stacked bar or horizontal bar, an optional objective line, a one-sentence
 * reading and the values as an accessible table.
 */
/** The container's width, so the chart is drawn at its real size and its text never shrinks. */
const useWidth = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  useEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      const next = Math.round(entry?.contentRect.width ?? 0);
      if (next > 0) setWidth(next);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return { ref, width };
};

export const ChartView = ({ data, hideTitle }: ChartViewProps) => {
  const { ref, width } = useWidth();
  const legend = data.series.length > 1 || (data.target && data.kind !== 'horizontal_bar');
  return (
    <figure data-view-surface className="rounded-xl border border-domino-cardBorder bg-domino-warm p-4">
      {!hideTitle && data.title && <p className="text-sm font-semibold text-domino-ink">{data.title}</p>}
      {data.source_note && <p className="mt-1 text-xs leading-5 text-domino-inkSoft">{data.source_note}</p>}
      {data.insight && <figcaption className="mt-2 text-sm leading-6 text-domino-ink">{data.insight}</figcaption>}
      {!hasChartValues(data) ? (
        <p className="mt-3 text-sm text-domino-inkSoft">{data.empty_message ?? 'Aucune donnée à afficher.'}</p>
      ) : (
        <>
          {legend && (
            <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-domino-inkSoft" aria-label="Légende">
              {data.series.map((series, index) => (
                <li key={series.key} className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-sm" style={{ background: color(index) }} aria-hidden="true" />{series.label}
                </li>
              ))}
              {data.target && data.kind !== 'horizontal_bar' && (
                <li className="flex items-center gap-1.5"><span className="w-3 border-t-2 border-dashed border-domino-ink" aria-hidden="true" />{data.target.label}</li>
              )}
            </ul>
          )}
          <div ref={ref} className="mt-3">
            {data.kind === 'horizontal_bar' ? <HorizontalPlot data={data} width={width} /> : <VerticalPlot data={data} width={width} />}
          </div>
          {/* A table ignores the 1px width of sr-only: its wrapper clips it, so a narrow screen never scrolls sideways. */}
          <div className="sr-only">
          <table>
            <caption>{data.title ?? 'Graphique'}</caption>
            <thead><tr><th>Catégorie</th>{data.series.map((series) => <th key={series.key}>{series.label}</th>)}</tr></thead>
            <tbody>
              {data.categories.map((category, index) => (
                <tr key={`${category}-${index}`}>
                  <th>{category}</th>
                  {data.series.map((series) => {
                    const value = series.values[index];
                    return <td key={series.key}>{value === null || value === undefined ? '—' : formatChartValue(value, data.unit)}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </>
      )}
    </figure>
  );
};
